import type { ReactNode } from 'react';

import { DISPLAY_FIGURE } from '@/features/workspace-overview/stateStyles';
import { PageHeader } from '@/shared/components/PageHeader';

/**
 * Every console page's header (spec 168): the page's name, and under it the
 * one fact an administrator came for. Never the band: the band is a
 * workspace's main page (spec 122), and the console is told apart by its own
 * shell (spec 167), so its headers stay on the page surface.
 *
 * The answer sits under the title rather than beside it, because the pages
 * that have controls (a period stepper) keep them on the right.
 */
export function ConsoleHeader({
  title,
  description,
  answer,
  actions,
}: {
  title: string;
  description: string;
  /** The page's answer: a `ConsoleAnswer`, absent while it is unknown. */
  answer?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="border-border flex flex-col gap-4 border-b pb-5">
      <PageHeader
        eyebrow="Organization"
        title={title}
        description={description}
        actions={actions}
      />
      {answer}
    </div>
  );
}

/**
 * A large figure, what it counts, and a line of detail (spec 168). The figure
 * is in the display face, as the Overview's tiles are, never the mono face: a
 * count is read, not copied. `visual` sits before it: Commit identity's ring.
 */
export function ConsoleAnswer({
  figure,
  words,
  detail,
  visual,
}: {
  figure: string;
  words: string;
  detail?: ReactNode;
  visual?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      {visual}
      <div className="flex min-w-0 flex-col gap-2">
        <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={`${DISPLAY_FIGURE} text-foreground tabular-nums`}>
            {figure}
          </span>
          <span className="text-muted-foreground text-base">{words}</span>
        </p>
        {detail ? (
          <div className="text-muted-foreground text-sm">{detail}</div>
        ) : null}
      </div>
    </div>
  );
}
